import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useClients, useUsers } from "@/lib/hooks";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EMPTY_PROJECT = {
  name: "",
  clientId: "",
  type: "PROJETO",
  status: "PLANEAMENTO",
  description: "",
  ownerId: "",
  memberIds: [],
  startDate: "",
  endDate: "",
  budget: 0,
  tags: [],
  goals: "",
  attachments: [],
};

export const PROJECT_TYPES = {
  PROJETO: "Projeto",
  CAMPANHA: "Campanha",
  RECORRENTE: "Trabalho recorrente",
  INTERNO: "Projeto interno",
};

export const PROJECT_STATUS = {
  PLANEAMENTO: { label: "Planeamento", color: "#94A3B8" },
  ATIVO: { label: "Ativo", color: "#4C9A8E" },
  EM_PAUSA: { label: "Em pausa", color: "#E5A94C" },
  CONCLUIDO: { label: "Concluído", color: "#806DAA" },
};

export function ProjectModal({ open, onOpenChange, project, fixedClientId }) {
  const queryClient = useQueryClient();
  const { data: clients = [] } = useClients();
  const { data: users = [] } = useUsers();
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [tagsText, setTagsText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = project ? { ...EMPTY_PROJECT, ...project } : { ...EMPTY_PROJECT, clientId: fixedClientId || "" };
    setForm(next);
    setTagsText((next.tags || []).join(", "));
  }, [open, project, fixedClientId]);

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const toggleMember = (memberId) => {
    set("memberIds", form.memberIds.includes(memberId)
      ? form.memberIds.filter((id) => id !== memberId)
      : [...form.memberIds, memberId]);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Indique o nome do projeto");
    if (!form.clientId) return toast.error("Selecione o cliente");
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget: Number(form.budget || 0),
        tags: tagsText.split(",").map((tag) => tag.trim()).filter(Boolean),
      };
      if (project?.id) await api.put(`/projects/${project.id}`, payload);
      else await api.post("/projects", payload);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success(project?.id ? "Projeto atualizado" : "Projeto criado");
      onOpenChange(false);
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-poppins">{project?.id ? "Editar projeto" : "Novo projeto ou campanha"}</DialogTitle>
          <DialogDescription>Liga cliente, equipa, objetivos, datas e orçamento.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome</Label><Input value={form.name} onChange={(event) => set("name", event.target.value)} className="mt-1.5" placeholder="Exemplo: Campanha de Natal" autoFocus /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Cliente</Label><Select value={form.clientId} onValueChange={(value) => set("clientId", value)} disabled={Boolean(fixedClientId)}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Selecionar cliente" /></SelectTrigger><SelectContent>{clients.map((client) => <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Tipo</Label><Select value={form.type} onValueChange={(value) => set("type", value)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PROJECT_TYPES).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Descrição</Label><Textarea value={form.description} onChange={(event) => set("description", event.target.value)} className="mt-1.5 min-h-[90px]" placeholder="Contexto e âmbito do trabalho" /></div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><Label>Estado</Label><Select value={form.status} onValueChange={(value) => set("status", value)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PROJECT_STATUS).map(([key, item]) => <SelectItem key={key} value={key}>{item.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Data de início</Label><Input type="date" value={form.startDate} onChange={(event) => set("startDate", event.target.value)} className="mt-1.5" /></div>
            <div><Label>Data final</Label><Input type="date" value={form.endDate} onChange={(event) => set("endDate", event.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Responsável</Label><Select value={form.ownerId || "none"} onValueChange={(value) => set("ownerId", value === "none" ? "" : value)}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Sem responsável" /></SelectTrigger><SelectContent><SelectItem value="none">Sem responsável</SelectItem>{users.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Orçamento</Label><Input type="number" min="0" step="0.01" value={form.budget} onChange={(event) => set("budget", event.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Objetivos</Label><Textarea value={form.goals} onChange={(event) => set("goals", event.target.value)} className="mt-1.5 min-h-[75px]" placeholder="O que define o sucesso deste projeto?" /></div>
          <div><Label>Etiquetas</Label><Input value={tagsText} onChange={(event) => setTagsText(event.target.value)} className="mt-1.5" placeholder="social media, lançamento, prioridade" /><p className="mt-1 text-xs text-muted-foreground">Separa as etiquetas por vírgulas.</p></div>
          <div>
            <Label>Equipa</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {users.map((member) => {
                const selected = form.memberIds.includes(member.id);
                return <button key={member.id} type="button" onClick={() => toggleMember(member.id)} className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2.5 text-xs ${selected ? "border-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/40"}`}><UserAvatar user={member} size="h-6 w-6" />{member.name.split(" ")[0]}{selected && <Check className="h-3 w-3 text-primary" />}</button>;
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90">{saving ? "A guardar..." : "Guardar projeto"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
